import type { HexTileMap, HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules';
import { type Building, isCity, type PlayerId, type SimContext, type SimState } from '../sim';
import { numberCities } from './cities';
import { type Fog, FogTracker } from './fog';
import { knownBuildings, type TileView, tileViewer } from './lastSeen';
import { type Names, NO_NAMES } from './names';
import { viewerState } from './viewerState';

/**
 * One tick as the local player sees it: everything the match screen's layers
 * and panels read, built once per tick and shared, so a derivation several
 * of them need (the fog-safe tile lookup, the buildings the player knows of,
 * city numbers) runs once.
 *
 * Real and view state are kept apart for fog safety:
 * - `view` is what may be drawn or described (hidden rival spies left out,
 *   ones disguised in the player's cities shown as their own units:
 *   `viewerState`). Everything on screen reads it.
 * - `real` is the sim's own state. Only vision, command feedback and the
 *   checks that must not be fooled by a disguise read it; nothing from it is
 *   ever shown as such.
 */
export interface Frame {
  /** Counts frames built for this match; a new frame has a new number. */
  readonly id: number;
  readonly tick: number;
  readonly playerId: PlayerId;
  /** The frozen match roster, for ownership colours. */
  readonly playerOrder: readonly PlayerId[];
  /** What to call a player on screen (their current name; ids are for keys only). */
  readonly playerName: (id: PlayerId) => string;
  readonly rules: RuleSet;
  readonly terrain: HexTileMap;
  /** `{ terrain, rules }`, the same object every frame. */
  readonly ctx: SimContext;
  /** The sim's real state. Never drawn or described: see above. */
  readonly real: SimState;
  /** The state as this player may see it: what everything on screen reads. */
  readonly view: SimState;
  readonly fog: Fog;
  /** Stable City 1/2/… numbers for this session (`numberCities`), sites included. */
  readonly cityNumbers: ReadonlyMap<string, number>;
  /** The player's own names for units, groups and cities (client-local), read live. */
  readonly names: Names;
  /**
   * What the player knows about a tile: live in sight, as last seen out of
   * sight, plus their own buildings (`lastSeen.ts` `tileViewer`).
   */
  readonly tileView: (key: HexTileMapKey) => TileView;
  /** The building the player knows of on a tile (`tileView(key).building`). */
  readonly knownBuilding: (key: HexTileMapKey) => Building | null;
  /** Every building the player knows of (`knownBuildings`), for rival borders and markers. */
  readonly knownBuildings: () => Building[];
}

export interface FrameSource {
  /** The sim's current (real) state. */
  getState: () => SimState;
  playerId: PlayerId;
  playerOrder: readonly PlayerId[];
  /** Read live, so a rename shows from the next frame read; defaults to the id. */
  playerName?: (id: PlayerId) => string;
  /** The player's own names, read live so a rename shows at once; none without. */
  names?: () => Names;
  rules: RuleSet;
  terrain: HexTileMap;
}

/**
 * The match's frames, for React to subscribe to (`useSyncExternalStore`):
 * `get()` builds the frame for the sim's current state on first read (keyed
 * on the state object and its tick: the host's runner changes one state in
 * place, but only inside `tick()`, which advances `state.tick`; a peer adopts
 * a new object with every snapshot), and `publish` tells subscribers the sim
 * has moved on.
 */
export interface FrameStore {
  get: () => Frame;
  subscribe: (listener: () => void) => () => void;
  /** Call after each sim change (`version` from the runner); repeats are ignored. */
  publish: (version: number) => void;
}

export function createFrameStore(source: FrameSource): FrameStore {
  const { playerId, playerOrder, rules, terrain } = source;
  const playerName = source.playerName ?? ((id: PlayerId) => id);
  const ctx: SimContext = { terrain, rules };
  const fog = new FogTracker();
  const numbers = new Map<string, number>();
  const listeners = new Set<() => void>();
  let frame: Frame | null = null;
  let published: number | null = null;
  let nextId = 0;

  function build(real: SimState): Frame {
    const view = viewerState(real, rules, playerId);
    const currentFog = fog.update(real, view, rules, terrain, playerId);
    // Sites are numbered too, so numbers follow founding order, not completion.
    numberCities(
      numbers,
      Object.values(view.buildings).filter((b) => b.owner === playerId && isCity(b, rules))
    );
    let lookup: ((key: HexTileMapKey) => TileView) | null = null;
    let known: Building[] | null = null;
    const tileView = (key: HexTileMapKey) => {
      lookup ??= tileViewer(fog.memory, view, rules, playerId, currentFog.sight);
      return lookup(key);
    };
    return {
      id: nextId++,
      tick: real.tick,
      playerId,
      playerOrder,
      playerName,
      rules,
      terrain,
      ctx,
      real,
      view,
      fog: currentFog,
      cityNumbers: numbers,
      get names() {
        return source.names?.() ?? NO_NAMES;
      },
      tileView,
      knownBuilding: (key) => tileView(key).building,
      knownBuildings: () => {
        known ??= knownBuildings(fog.memory, view, rules, playerId, currentFog.sight);
        return known;
      },
    };
  }

  return {
    get() {
      const real = source.getState();
      if (!frame || frame.real !== real || frame.tick !== real.tick) frame = build(real);
      return frame;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    publish(version) {
      if (version === published) return;
      published = version;
      for (const listener of listeners) listener();
    },
  };
}
